import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// =====================================================
// Types
// =====================================================

export type ForecastMode = 'expected' | 'conservative' | 'optimistic';

export interface OptimizationRunSummary {
  id: string;
  location_id: string;
  period_start: string;
  period_end: string;
  scenario_code: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  solver_status: string | null;
  objective_value: number | null;
  solve_time_ms: number | null;
  total_fot: number | null;
  coverage_score: number | null;
  understaffed_hours: number | null;
  overtime_hours: number | null;
  created_at: string;
  applied_as_request_id: string | null;
  parent_run_id: string | null;
  llm_explanation: string | null;
  llm_agents_output: MultiAgentReport | null;
  /** null если запуск был на все должности; массив UUID если per-position. */
  position_ids: string[] | null;
}

export interface MultiAgentReport {
  sales: string;
  schedule: string;
  risks: string;
  summary: string;
  model: string;
  generated_at: string;
  usage?: Record<string, unknown>;
}

export interface SolverAssignment {
  employee_id: string;
  date: string;
  shift_id: string;
}

export interface OptimizationRunDetail extends OptimizationRunSummary {
  input_snapshot: Record<string, unknown>;
  constraints_snapshot: Record<string, unknown>;
  objective_weights: Record<string, unknown>;
  solution: {
    assignments?: SolverAssignment[];
    metrics?: {
      total_fot?: number;
      coverage_score?: number;
      understaffed_hours?: number;
      overtime_hours?: number;
    };
  } | null;
  notes: string | null;
}

export interface CreateRunPayload {
  location_id: string;
  period_start: string; // YYYY-MM-DD
  period_end: string;
  weights?: {
    fot?: number;
    understaffing?: number;
    overtime?: number;
    fairness?: number;
    payroll_excess?: number;
  };
  solver_timeout_seconds?: number;
  default_target_revenue_per_hour?: number;
  scenario_code?: string;
  forecast_mode?: ForecastMode;
  parent_run_id?: string;
  /** Per-position режим: undefined/[] = все активные должности (default).
   *  Если массив непустой — оптимизация ограничится этими позициями. */
  position_ids?: string[];
  /** Per-department scope: undefined/[] = все подразделения (default).
   *  Если массив непустой — пул сотрудников ограничится этими подразделениями. */
  department_ids?: string[];
  /** Целевая доля ФОТ от выручки (0..1, например 0.25 = 25%).
   *  Если задан — solver добавит soft constraint total_FOT ≤ ratio×revenue. */
  target_payroll_ratio?: number;
  /** Свободная заметка управляющего для ИИ-агентов: банкеты, события,
   *  ремонты — контекст, которого нет в данных. */
  manager_notes?: string;
}

export interface BranchRunPayload {
  weights?: {
    fot?: number;
    understaffing?: number;
    overtime?: number;
    fairness?: number;
    payroll_excess?: number;
  };
  scenario_code?: string;
  solver_timeout_seconds?: number;
  forecast_mode?: ForecastMode;
  /** Override per-position scope от родителя.
   *   undefined → унаследовать,
   *   [] → расширить на все,
   *   [uuid...] → новый список. */
  position_ids?: string[];
  /** Override per-department scope от родителя (та же семантика, что position_ids). */
  department_ids?: string[];
  /** Override target ratio. undefined = унаследовать из parent. */
  target_payroll_ratio?: number;
}

export interface LlmStatus {
  available: boolean;
  model: string;
  multi_agent_available?: boolean;
}

export interface ExplainResult {
  explanation: string;
  model: string;
  input_tokens: number;
  cache_read_tokens: number;
  cache_creation_tokens: number;
  output_tokens: number;
}

export interface ApplyRunResult {
  run_id: string;
  applied_as_request_id: string;
  requests: Array<{
    id: string;
    department_id: string;
    department_name: string | null;
    items_count: number;
    cancelled_pending_request_id: string | null;
  }>;
  skipped_employees: string[];
}

// =====================================================
// Query Keys
// =====================================================

const KEYS = {
  all: ['optimization-runs'] as const,
  list: (locationId?: string) => [...KEYS.all, 'list', locationId] as const,
  detail: (id: string) => [...KEYS.all, 'detail', id] as const,
};

// =====================================================
// Hooks
// =====================================================

export const useOptimizationRuns = (locationId?: string) =>
  useQuery<OptimizationRunSummary[]>({
    queryKey: KEYS.list(locationId),
    queryFn: () =>
      api.get('/api/v1/optimization/runs', {
        params: locationId ? { location_id: locationId } : undefined,
      }),
  });

/**
 * Polling-aware run detail. Когда status='running', refetch каждые 2s.
 * Если waitForExplanation=true и LLM включён — polling продолжается ещё
 * до 60 секунд после created_at, пока llm_explanation не появится.
 */
export const useOptimizationRun = (
  id: string | null,
  options: {
    enablePolling?: boolean;
    waitForExplanation?: boolean;
    /** Если true — продолжаем polling пока не появится llm_agents_output.summary */
    waitForAgentsReport?: boolean;
  } = {},
) =>
  useQuery<OptimizationRunDetail>({
    queryKey: KEYS.detail(id ?? ''),
    queryFn: () => api.get(`/api/v1/optimization/runs/${id}`),
    enabled: !!id,
    refetchInterval: (query) => {
      if (!options.enablePolling) return false;
      const data = query.state.data;
      const status = data?.status;
      if (status === 'running' || status === 'pending') return 2000;
      if (status === 'completed' && data?.created_at) {
        const elapsed = Date.now() - new Date(data.created_at).getTime();
        const needExplanation =
          options.waitForExplanation && !data?.llm_explanation;
        const needAgents =
          options.waitForAgentsReport && !data?.llm_agents_output?.summary;
        if ((needExplanation || needAgents) && elapsed < 180_000) return 3000;
      }
      return false;
    },
  });

export const useCreateOptimizationRun = () => {
  const qc = useQueryClient();
  return useMutation<OptimizationRunDetail, Error, CreateRunPayload>({
    mutationFn: (payload) => api.post('/api/v1/optimization/runs', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

export const useApplyOptimizationRun = () => {
  const qc = useQueryClient();
  return useMutation<ApplyRunResult, Error, string>({
    mutationFn: (runId) => api.post(`/api/v1/optimization/runs/${runId}/apply`),
    onSuccess: (_, runId) => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      qc.invalidateQueries({ queryKey: KEYS.detail(runId) });
      qc.invalidateQueries({ queryKey: ['schedule-change-requests'] });
    },
  });
};

/**
 * What-if: создать новый run от родителя с override-ми весов или forecast_mode.
 */
export const useBranchOptimizationRun = () => {
  const qc = useQueryClient();
  return useMutation<
    OptimizationRunDetail,
    Error,
    { parentRunId: string; body: BranchRunPayload }
  >({
    mutationFn: ({ parentRunId, body }) =>
      api.post(`/api/v1/optimization/runs/${parentRunId}/branch`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

// =====================================================
// LLM Explainer
// =====================================================

export const useLlmStatus = () =>
  useQuery<LlmStatus>({
    queryKey: ['optimization-llm-status'],
    queryFn: () => api.get('/api/v1/optimization/llm/status'),
    staleTime: 5 * 60_000,
  });

export const useRegenerateExplanation = () => {
  const qc = useQueryClient();
  return useMutation<ExplainResult, Error, string>({
    mutationFn: (runId) =>
      api.post(`/api/v1/optimization/runs/${runId}/explain?force=true`),
    onSuccess: (_, runId) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(runId) });
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

/**
 * Запускает (или перегенерирует) multi-agent отчёт.
 * Backend сохраняет результат в optimization_runs.llm_agents_output.
 */
export const useMultiAgentReport = () => {
  const qc = useQueryClient();
  return useMutation<MultiAgentReport, Error, { runId: string; force?: boolean }>({
    mutationFn: ({ runId, force }) =>
      api.post(
        `/api/v1/optimization/runs/${runId}/multi-agent-explain${
          force ? '?force=true' : ''
        }`,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.runId) });
      qc.invalidateQueries({ queryKey: KEYS.all });
    },
  });
};

// =====================================================
// Forecast status (bulk, для UI-селекторов)
// =====================================================

export interface ForecastStatusEntry {
  location_id: string;
  forecast_department_id: string | null;
  source: 'organization' | 'override' | null;
}

export const useForecastStatuses = () =>
  useQuery<ForecastStatusEntry[]>({
    queryKey: ['optimization-forecast-status'],
    queryFn: () => api.get('/api/v1/optimization/locations/forecast-status'),
    staleTime: 60_000,
  });
